import type { AgentID, Applog, IShare } from '@wovin/core'
import type { AgentStateClass } from './AgentState'
import type { CryptoKeys, ECDHKeyPair } from '@wovin/crypto'
import { arrayBufferToBase64, base64ToArray, base64ToArrayBuffer, DefaultFalse } from '@wovin/utils'
import { Logger } from 'besonders-logger'
import { base64pad } from 'iso-base/rfc4648'
import { untag } from 'iso-base/varint'
import { EdDSASigner } from 'iso-signatures/signers/eddsa.js'
import {
	aesUtils,
	createECDHderivationKeyPair,
	createEdDSAkeypair,
	createMnemonic,
	deriveAesViaHKDFKey,
	deriveSharedEncryptionKey,
	EdKeypairInstanceFromSecretKeyBytes,
	SymmAlg,
} from '@wovin/crypto'
import { importPublicDerivationKey } from '../../ipfs/share-sync'
import { notifyToast, ToastVariant } from '../../ui/utils-ui'
import { stateDB } from '../local-state'
import { updateAgentState, useAgent } from './AgentState'

const { WARN, LOG, DEBUG, VERBOSE, ERROR } = Logger.setup(Logger.DEBUG) // eslint-disable-line unused-imports/no-unused-vars

export type { CryptoKeys, ECDHKeyPair } from '@wovin/crypto'

export const decryptWithAesSharedKey = async function decryptWithAesSharedKey(
	encByteArray: Uint8Array,
	aesKey: CryptoKey,
	as: 'array' | 'parsed' | 'string' = 'array',
) {
	// const aesKey = currentPubSub.encryptedWith ?? (currentPubSub as IShare).sharedKey
	if (!aesKey) {
		throw ERROR('[decrypt] missing aesKey', { encByteArray })
		// throw new Error('[decrypt] missing info') // ERROR from logger is now throwable
	}

	// @expede advises against fixed iv https://github.com/fission-codes/keystore-idb/issues/70#issuecomment-1624377859
	// so i recreated the way @odd / keystoreIDB create random iv and join the iv unencrypted at the front of the encrypted payload
	let oddDecrypted: Uint8Array
	const dec = new TextDecoder('utf-8')

	const { crypto } = useAgent()
	VERBOSE('decryptWithAesSharedKey', { encByteArray, aesKey, crypto })
	try {
		oddDecrypted = await aesUtils.decrypt(encByteArray, aesKey, SymmAlg.AES_GCM)
	} catch (error) {
		ERROR('aes decrypt error', { error })
	}

	const oddDecoded = dec.decode(oddDecrypted)
	VERBOSE('decryptWithAesSharedKey', { oddDecrypted, oddDecoded })
	return as === 'array' ? oddDecrypted as Uint8Array : as === 'parsed' ? JSON.parse(oddDecoded) : oddDecoded
}

// personal means only for the current agent derived using the public and private keys from the same keypair
// apparently its fine: https://crypto.stackexchange.com/a/26343/104653
export const getAESkeyPersonal = async function getAESkeyForPersonalEncryption(keypair?: ECDHKeyPair) {
	// TODO review when and where this is appropriate
	// subsequent derivations for each context may prove more secure
	const { privateKey, publicKey } = keypair ?? await useAgent().getOrSetDerivationKeypair()
	const aesEncryptionKey = deriveSharedEncryptionKey(privateKey, publicKey, false)
	VERBOSE({ privateKey, publicKey, aesEncryptionKey })
	return aesEncryptionKey
}

export const getAESkeyForEncryptedApplogs = async function getAESkeyForEncryptedApplogs(nonEncryptedLogs: Applog[]) {
	const agent = useAgent()
	const { ag, getOrSetDerivationKeypair, getSharedKeyApplogForCurrentAgent } = agent
	const { privateKey: priDerivationKey } = await getOrSetDerivationKeypair()
	const sharedKeyApplog = getSharedKeyApplogForCurrentAgent(nonEncryptedLogs)

	if (!sharedKeyApplog) return ERROR('no sharedKey Found for current agent', { nonEncryptedLogs, priDerivationKey, ag })

	const { vl: encryptedSharedKey, ag: publisherAg } = sharedKeyApplog as Applog

	const publicKeyForPublisher = (nonEncryptedLogs as Applog[]).find(eachLog =>
		(eachLog?.at as string) === 'agent/jwkd'
		&& (eachLog?.ag as string) === publisherAg,
	)?.vl

	const publicKeyECDHforPublisher = (nonEncryptedLogs as Applog[]).find(eachLog =>
		(eachLog?.at as string) === 'agent/ecdh'
		&& (eachLog?.ag as string) === publisherAg,
	)?.vl

	const publicKeyFromApplogs = await importPublicDerivationKey(publicKeyForPublisher as string) // TODO discuss depricate jwkd
	const derivedKeyDecryptionKey = await deriveSharedEncryptionKey(priDerivationKey, publicKeyFromApplogs)
	const encryptedKeyUintArr = base64ToArray(encryptedSharedKey as string)
	DEBUG({ publicKeyFromApplogs, publicKeyECDHforPublisher, derivedKeyDecryptionKey, encryptedSharedKey })

	const decryptedSharedKeyBase64 = arrayBufferToBase64(await decryptWithAesSharedKey(encryptedKeyUintArr, derivedKeyDecryptionKey))
	const decryptedSharedKeyUintArrForImport = base64ToArrayBuffer(decryptedSharedKeyBase64)
	DEBUG({ decryptedSharedKeyBase64, decryptedSharedKeyUintArrForImport })

	const aesKey = await globalThis.crypto.subtle.importKey(
		'raw',
		decryptedSharedKeyUintArrForImport, // this has been converted from b64 to uint8[] then decrypted then converted again to uint and then imported
		{
			name: 'AES-GCM',
			length: 256,
		},
		false,
		['encrypt', 'decrypt'],
	)
	DEBUG('shared enc', { aesKey, decryptedSharedKeyBase64, encryptedSharedKey })
	return { aesKey, publisherAg }
}

export const createAESkeyForShare = async function createAESkeyForShare(share: IShare) {
	const agent = useAgent()
	const { ag, did, getHKDFkey } = agent
	const rootHKDFderivationKey = await getHKDFkey()
	const salt = share.id
	const info = `note3share-ag:${ag}-${did}-shareCounter:${share.pubCounter}`

	const aesKey = await deriveAesViaHKDFKey(rootHKDFderivationKey, info, salt, true) // needs to be extractable for distribution and will only be stored encrypted
	DEBUG('shared enc', { aesKey, rootHKDFderivationKey, info, salt })
	return aesKey
}

export async function storeKeyPairInIDB(keyPair: CryptoKeyPair, ag: AgentID) {
	if (keyPair /* && TODO check type */) await stateDB.cryptokeys.put({ ag, type: 'trust', keys: { ...keyPair } })
}
export async function getDerivationKeypairFromIDB(agent: AgentStateClass) {
	const { expectedAgentHash /* , ag */ } = agent
	// if (expectedAgentHash !== ag) WARN('divergent expected and ag in getDerivationKeypairFromIDB', { expectedAgentHash, ag })
	return (await stateDB.cryptokeys.get(
		[expectedAgentHash, 'derivation'], // ? using expected here so that the indexdb can hold agent info for more than one agent
	))?.keys as CryptoKeyPair | undefined
}
export async function getDecryptedSecretFromIDB(agent: AgentStateClass, crypto?: CryptoKeys) {
	crypto = crypto ?? agent.crypto
	const fromIDB = (await stateDB.cryptokeys.get([agent.expectedAgentHash, 'eddsa']))?.keys as { secretKey: Uint8Array }
	const encryptedfromIDB = fromIDB?.secretKey
	DEBUG({ fromIDB })
	if (encryptedfromIDB) {
		const personalEncryptionKey = await getAESkeyPersonal(crypto.ecdh as CryptoKeyPair)
		VERBOSE('Attempting Decryption', { encryptedfromIDB, personalEncryptionKey })
		const decryptedFromIDB = await aesUtils.decrypt(encryptedfromIDB, personalEncryptionKey, SymmAlg.AES_GCM)
		DEBUG({ fromIDB, decryptedFromIDB })
		return decryptedFromIDB
	} else {
		WARN('decrypted Secret Not Found')
	}
	return null
}
export async function getEdDSAfromIDB(agent: AgentStateClass, crypto?: CryptoKeys) {
	crypto = crypto ?? agent.crypto
	const decryptedFromIDB = await getDecryptedSecretFromIDB(agent, crypto)
	if (decryptedFromIDB) {
		const edKeypairInstance = EdKeypairInstanceFromSecretKeyBytes(decryptedFromIDB)
		DEBUG({ edKeypairInstance })
		return edKeypairInstance
	}
	return null
}
export async function getMnemonicFromIDB(agent: AgentStateClass, agOveride?: string, crypto?: CryptoKeys) {
	crypto = crypto ?? agent.crypto
	const fromIDB = (await stateDB.cryptokeys.get([agOveride ?? agent.expectedAgentHash ?? agent.ag, 'mnemonic']))?.keys

	// @ts-expect-error too lazy
	const encryptedfromIDB = fromIDB?.encryptedMnemonic
	DEBUG({ fromIDB })
	if (encryptedfromIDB) {
		const personalEncryptionKey = await getAESkeyPersonal(crypto.ecdh as CryptoKeyPair)
		const decryptedFromIDB = await aesUtils.decrypt(encryptedfromIDB, personalEncryptionKey, SymmAlg.AES_GCM)
		return new TextDecoder('utf-8').decode(decryptedFromIDB)
	} else {
		const toastMsg = 'Missing mneumonic'
		notifyToast(toastMsg)
		ERROR(toastMsg, { agent })
	}
}
export const initializeCryptoKeypairs = async function initializeCryptoKeypairs(
	agent: AgentStateClass,
	newMnemonic?: string[],
	skipWarning = DefaultFalse,
) {
	const crypto: CryptoKeys = agent.crypto ?? {}
	DEBUG('[CryptoKeys] default', { crypto })

	if (newMnemonic?.length > 10) {
		crypto.ecdh = await createECDHderivationKeyPair(newMnemonic)
		crypto.eddsa = await createEdDSAkeypair(newMnemonic, agent.agentStringNoDID)
		VERBOSE('created ecdh and EdDSAkeypair from passed "valid" mneumonic', { crypto })
	} else {
		crypto.ecdh = await getDerivationKeypairFromIDB(agent)
		DEBUG('[CryptoKeys] ecdh?', { crypto })

		if (!crypto.ecdh) {
			newMnemonic = createMnemonic(24)
			crypto.ecdh = await createECDHderivationKeyPair(newMnemonic)
			crypto.eddsa = await createEdDSAkeypair(newMnemonic, agent.agentStringNoDID) // TODO derive from ecdh instead maybe
			VERBOSE('created ecdh and EdDSAkeypair from new mneumonic', { crypto })
		} else {
			crypto.eddsa = await getEdDSAfromIDB(agent, crypto)
			VERBOSE('createdEdDSAkeypair from ecdh in IDB', { crypto })
		}
	}

	const personalEncryptionKey = await getAESkeyPersonal(crypto.ecdh as CryptoKeyPair) // crypto.ecdh could be from idb or fresh
	// const personalHKDFkey = await createHKDFkey(newMnemonic)
	// crypto.hkdf = personalHKDFkey
	let encryptedEdSecretKey
	if (crypto.eddsa) {
		// HACK: we need raw private key
		const eddsaPkEncoded = crypto.eddsa.export()
		const eddsaPk = untag(EdDSASigner.code, base64pad.decode(eddsaPkEncoded))
		encryptedEdSecretKey = await aesUtils.encrypt(
			eddsaPk,
			personalEncryptionKey,
			SymmAlg.AES_GCM,
		)
	}
	// ready in case its needed: ecdsa
	// const signingKeypairFromMnemonic = await getECDSAkeypairFromHashArray(encHashedMnu)
	// crypto.ecdsa = signingKeypairFromMnemonic

	const updated = { ...crypto }

	updateAgentState({ crypto: updated })

	const didAfter = agent.did
	const ag = agent.ag
	const expectedBefor = agent.expectedAgentHash
	agent.expectedAgentHash = ag
	DEBUG('init Keys', { ag, didAfter, agExpected: agent.expectedAgentHash })
	if (!skipWarning && expectedBefor && expectedBefor !== ag) {
		notifyToast(
			`non matching expected and resulting ag \n (ag has changed since last app load from ${expectedBefor} -> ${ag})`,
			ToastVariant.warning,
			90000,
		)
	}
	// ? check this idempotent put approach
	if (crypto.eddsa) await stateDB.cryptokeys.put({ ag, type: 'eddsa', keys: { secretKey: encryptedEdSecretKey } })
	if (crypto.ecdh) await stateDB.cryptokeys.put({ ag, type: 'derivation', keys: crypto.ecdh })
	// await stateDB.cryptokeys.put({ ag, type: 'signing-ecdsa', keys: crypto.eddsa }) // keep around

	if (newMnemonic?.length) {
		const encryptedMnemonic = await aesUtils.encrypt(
			new TextEncoder().encode(newMnemonic.toString()),
			personalEncryptionKey,
			SymmAlg.AES_GCM,
		)
		await stateDB.cryptokeys.put({ ag, type: 'mnemonic', keys: { encryptedMnemonic } })
	}

	DEBUG('initialized', { crypto })
	await agent.ensureAgentAtoms()
	return crypto.ecdh
}
